/**
 * Rol kapısı — giriş öncesi "Müşteriyim / Firmayım".
 *
 * Sayfası olmayan modül: açılışta bir kez çalışır, kendi ekranını yönetir.
 *
 * KAPI AÇIK/KAPALI: aşağıdaki GATE_ENABLED. false = rol sorulmaz, giriş ekranı
 * doğrudan e-posta formuyla açılır. Ekran, çeviriler ve seçim mantığı yerinde
 * kalır; hiçbir şey silinmez.
 *
 * Kapı kapalıyken hesap türü DEĞİŞMEZ: yeni kayıtlar 'firm' olarak açılır ve
 * veritabanındaki kilitler çalışmaya devam eder.
 *
 * NOT: giriş formu HTML'de varsayılan olarak GÖRÜNÜR, rol ekranı gizlidir.
 * Tersi olsaydı bu modül yüklenemediğinde kullanıcı tıklanmayan bir ekranda
 * kilitli kalırdı — giriş ekranı hiçbir zaman isteğe bağlı bir modüle bağımlı
 * olmamalı. Rol ekranını yalnızca burası açar.
 *
 * KALDIRMAK İÇİN: bu klasörü sil, shell/index.ts listesinden satırı çıkar,
 * index.html'deki #sb-role-wrap bloğunu kaldır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { TEXTS } from './texts.ts';
import {
  applyCustomerUi, clear, current, isCustomer, managerName,
  setChoice, setFromDb, wrapUpdateDelBtn, type AccountType,
} from './account.ts';

export const GATE_ENABLED = true;

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/** Giriş ekranında önce rol sorulur; kapı kapalıysa doğrudan e-posta formu. */
function showRoleScreen(): void {
  const role = el('sb-role-wrap');
  const auth = el('sb-auth-wrap');
  const user = el('sb-user-wrap');

  if (!GATE_ENABLED) {
    if (role) role.style.display = 'none';
    if (auth) auth.style.display = '';
    if (user) user.style.display = 'none';
    const back = el('role-back-btn');
    if (back) back.style.display = 'none';
    legacy.authTab('login');
    return;
  }

  if (role) role.style.display = '';
  if (auth) auth.style.display = 'none';
  if (user) user.style.display = 'none';

  const labels: Record<string, string> = {
    'role-q': 'role_q', 'role-hint': 'role_hint',
    'role-cust-t': 'role_cust', 'role-cust-d': 'role_cust_d',
    'role-firm-t': 'role_firm', 'role-firm-d': 'role_firm_d',
  };
  for (const id of Object.keys(labels)) {
    const node = el(id);
    if (node) node.textContent = legacy.t(labels[id]);
  }
}

/** Rol seçildi — e-posta formunu aç. Tür kayıt anında hesaba yazılır. */
function chooseAccountType(t: unknown): void {
  if (t !== 'customer' && t !== 'firm') return;
  setChoice(t as AccountType);
  const role = el('sb-role-wrap');
  const auth = el('sb-auth-wrap');
  if (role) role.style.display = 'none';
  if (auth) auth.style.display = '';
  const back = el('role-back-btn');
  if (back) { back.style.display = ''; back.textContent = legacy.t('role_back'); }
  legacy.authTab('login');
}

/** Rol sorusunun atlandığı özel durumlar: şifre yenileme ve incelemeye alınmış
 *  hesap. İkisinde de kimlik zaten belli, e-posta formu doğrudan gösterilir. */
function hideRoleScreen(): void {
  const role = el('sb-role-wrap');
  if (role) role.style.display = 'none';
}

export default defineFeature({
  id: 'account-type',
  label: 'Hesap türü ve rol kapısı',
  view: null,

  boot: () => {
    legacy.addTranslations(TEXTS);
    /* core.js yetki düğmelerini yeniden çizdiğinde kısıt kaybolmasın. */
    wrapUpdateDelBtn();
    /* Kabuk ERTELENMİŞ bir modül: DOM ayrıştırması bitmiş, DOMContentLoaded
       henüz atmamış olur. Rol ekranını burada açmak güvenli.
       Oturum zaten varsa sbAfterLogin bunu gizleyip hesap listesini açar. */
    if (GATE_ENABLED) { try { showRoleScreen(); } catch { /* DOM hazır değilse geç */ } }
  },

  /* Eski dünyanın çağırdığı adlar: index.html'deki onclick'ler ve auth.js. */
  legacyGlobals: {
    chooseAccountType: (t: unknown) => chooseAccountType(t),
    backToRoleScreen: () => showRoleScreen(),
    showRoleScreen: () => showRoleScreen(),
    atHideRoleScreen: () => hideRoleScreen(),
    atClear: () => clear(),
    atSetFromDb: (t: unknown) => setFromDb(t),
    atManagerName: (t: unknown) => managerName(t),
    atCurrent: () => current(),
    atIsCustomer: () => isCustomer(),
    atApplyCustomerUi: () => applyCustomerUi(),
  },

  owns: { tables: ['profiles.account_type'] },
});
